import {
  Checkbox,
  CheckboxGroup,
  CheckboxGroupProps,
  List,
  ListItem,
  ListItemProps,
  ListProps,
} from "@chakra-ui/react";
import { upperFirst } from "lodash";
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { AssertionError } from "utils/assertions";

export function getStringValue<TOptions>(value: TOptions): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

type LabelsForOption<TOptions> = TOptions extends string | number | symbol
  ? Partial<Record<TOptions, ReactNode>>
  : never;

export type BaseCheckboxOptionsSelectProps<TOptions> = {
  getLabel?: (option: TOptions) => ReactNode;
  labels?: LabelsForOption<TOptions>;
  onChange: (option: Exclude<TOptions, null | undefined>[]) => void;
  options: TOptions[] | readonly TOptions[];
  value?: TOptions[] | null;
};

export function useCheckboxOptionsSelect<TOptions>(
  props: BaseCheckboxOptionsSelectProps<TOptions>,
) {
  const { getLabel, labels, options, onChange, value } = props;

  const fullOptions = useMemo(
    () =>
      options.map((option) => {
        const strValue = getStringValue(option);
        return {
          label: getLabel?.(option) || labels?.[option] || upperFirst(strValue),
          raw: option,
          value: strValue,
        };
      }),
    [options, labels, getLabel],
  );

  const strValue = useMemo(() => value?.map(getStringValue) || [], [value]);

  const optionsByStrValue = useMemo(
    () =>
      (options as TOptions[]).reduce<{ [strValueKey: string]: TOptions }>(
        (acc, opt) => {
          acc[getStringValue(opt)] = opt;
          return acc;
        },
        {},
      ),
    [options],
  );

  const handleChange = useCallback(
    (nextValue: string[]) => {
      const optVal = nextValue.map((v) => {
        const opt = optionsByStrValue[v];
        if (opt === undefined) {
          throw new AssertionError(`Could not get option for value: ${v}`);
        }
        return opt;
      }) as Exclude<TOptions, null | undefined>[];
      onChange(optVal);
    },
    [optionsByStrValue, onChange],
  );

  return {
    fullOptions,
    handleChange,
    strValue,
  };
}

export type CheckboxOptionsSelectProps<TOptions> = Omit<
  CheckboxGroupProps,
  "children" | "onChange" | "value"
> &
  BaseCheckboxOptionsSelectProps<TOptions> & {
    listProps?: ListProps;
    listItemProps?: ListItemProps;
  };

function CheckboxOptionsSelectInner<TOptions>(
  {
    getLabel,
    labels,
    listProps,
    listItemProps,
    onChange,
    options,
    value,
    ...rest
  }: CheckboxOptionsSelectProps<TOptions>,
  ref: ForwardedRef<HTMLUListElement>,
) {
  const { fullOptions, handleChange, strValue } = useCheckboxOptionsSelect({
    getLabel,
    labels,
    onChange,
    options,
    value,
  });
  return (
    <CheckboxGroup value={strValue} onChange={handleChange} {...rest}>
      <List ref={ref} mx={-2} {...listProps}>
        {fullOptions.map((option) => (
          <ListItem key={option.value} mx={2} {...listItemProps}>
            <Checkbox value={option.value}>{option.label}</Checkbox>
          </ListItem>
        ))}
      </List>
    </CheckboxGroup>
  );
}

export default forwardRef(CheckboxOptionsSelectInner);

type BooleanObject<TOption extends string> = Partial<{
  [key in TOption]: boolean;
}>;

type NullableBooleanObject<TOption extends string> = Partial<{
  [key in TOption]: boolean | null;
}>;

export function getBooleanObjectProps<TOption extends string>(
  options: ReadonlyArray<TOption>,
  {
    value,
    onChange,
  }: {
    value: NullableBooleanObject<TOption> | null | undefined;
    onChange: (value: BooleanObject<TOption>) => void;
  },
) {
  const arrValue = (Object.keys(value || {}) as TOption[]).reduce<TOption[]>(
    (acc, v) => {
      if (value?.[v]) acc.push(v);
      return acc;
    },
    [],
  );

  const onArrChange = (newValue: TOption[]) => {
    const newObjValue = options.reduce<BooleanObject<TOption>>((acc, v) => {
      acc[v] = newValue.includes(v);
      return acc;
    }, {} as BooleanObject<TOption>);
    onChange(newObjValue);
  };

  return {
    options,
    value: arrValue,
    onChange: onArrChange,
  };
}
